// src/pages/Accounting/Reconcile/Session/PrintReport.tsx

import type React from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PALETTE } from "../constants";
import type { Account } from "../types";
import { formatDateLong } from "../utils";
import type { SessionTransactionRich } from "./sessionTypes";
import { TRANSACTION_KIND_LABELS } from "./sessionTypes";

interface PrintReportProps {
  companyName: string;
  account: Account | undefined;
  periodEnd: string;
  transactions: SessionTransactionRich[];
  clearedIds: Set<string>;
}

const printCss = `
#novala-print-root { display: none; }
@media print {
  body > *:not(#novala-print-root) { display: none !important; }
  #novala-print-root { display: block !important; }
  #novala-print-root tr { page-break-inside: avoid; }
  @page { size: letter; margin: 18mm; }
}
`;

export function PrintReport({
  companyName,
  account,
  periodEnd,
  transactions,
  clearedIds,
}: PrintReportProps) {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let el = document.getElementById("novala-print-root");
    let created = false;
    if (!el) {
      el = document.createElement("div");
      el.id = "novala-print-root";
      document.body.appendChild(el);
      created = true;
    }
    setContainer(el);
    return () => {
      if (created && el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    };
  }, []);

  if (!container) return null;

  const currency = account?.currency ?? "CAD";
  const accountLabel = account
    ? account.last4
      ? `${account.name} \u00b7 ****${account.last4}`
      : account.name
    : "";
  const periodLabel = `Period Ending ${formatDateLong(periodEnd)}`;

  return createPortal(
    <>
      <style>{printCss}</style>
      <div style={wrapStyle}>
        <div style={headerStyle}>
          <h1 style={companyStyle}>{companyName}</h1>
          <div style={subHeaderStyle}>
            {accountLabel ? `${accountLabel}, ${periodLabel}` : periodLabel}
          </div>
        </div>

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Ref no.</th>
              <th style={thStyle}>Payee</th>
              <th style={thStyle}>Memo</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Payment ({currency})</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Deposit ({currency})</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => {
              const cleared = clearedIds.has(tx.id);
              const isPayment = tx.amount < 0;
              const isDeposit = tx.amount > 0;
              const statusLabel = cleared ? "Cleared" : "Pending";
              const statusColor = cleared ? PALETTE.tealDark : PALETTE.ink4;
              return (
                <tr key={tx.id}>
                  <td style={tdStyle}>{formatIsoToDayMonthYear(tx.date)}</td>
                  <td style={tdStyle}>{TRANSACTION_KIND_LABELS[tx.kind]}</td>
                  <td style={tdStyle}>{tx.refNo ?? ""}</td>
                  <td style={tdStyle}>{tx.payee}</td>
                  <td style={tdStyle}>{tx.memo ?? ""}</td>
                  <td style={tdNumStyle}>{isPayment ? Math.abs(tx.amount).toFixed(2) : ""}</td>
                  <td style={tdNumStyle}>{isDeposit ? tx.amount.toFixed(2) : ""}</td>
                  <td style={{ ...tdStyle, color: statusColor, fontWeight: 600 }}>{statusLabel}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>,
    container,
  );
}

function formatIsoToDayMonthYear(iso: string): string {
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return iso;
  return `${dd}/${mm}/${yyyy}`;
}

const wrapStyle: React.CSSProperties = {
  fontFamily: "-apple-system, Segoe UI, Roboto, sans-serif",
  color: PALETTE.ink,
  background: "#FFFFFF",
};
const headerStyle: React.CSSProperties = {
  textAlign: "center",
  marginBottom: 28,
};
const companyStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: "0 0 8px",
  letterSpacing: "-0.01em",
};
const subHeaderStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: PALETTE.ink,
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 11,
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "9px 7px",
  borderBottom: `2px solid ${PALETTE.ink}`,
  color: PALETTE.ink,
  fontWeight: 700,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const tdStyle: React.CSSProperties = {
  padding: "7px 7px",
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  color: PALETTE.ink2,
  fontWeight: 500,
  verticalAlign: "top",
};
const tdNumStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  color: PALETTE.ink,
  fontWeight: 600,
};